import { Injectable, inject } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationExtras, Router } from '@angular/router';
import { StorageService } from './util/storage.service';
import { getRoutePathFor } from '../utils/route';
import { ConfigService } from './util/config.service';
import { OUTLAW_COOKIES } from '../consts/cookie';
import { OUTLAW_ROUTES } from '../consts';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  readonly document = inject<Document>(DOCUMENT);
  readonly router = inject(Router);
  private storageService = inject(StorageService);
  private configService = inject(ConfigService);

  socialAuthUrl = this.configService.apiConfig.socialAuthUrl;

  get window(): Window {
    return this.document.defaultView as any;
  }

  openPage(url: string, target: string): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        resolve(!!this.window.open(url, target));
      } catch (e) {
        reject(e);
      }
    });
  }

  openOnSamePage(link: string): Promise<boolean> {
    return this.openPage(link, '_self');
  }

  openOnExternalPage(link: string): Promise<boolean> {
    return this.openPage(link, '_blank');
  }

  reloadPage(url: string): void {
    return this.redirectToCustomDeeplink(url);
  }

  reloadCurrentPage(): void {
    this.document.location.reload();
  }

  externalRedirect(url: string): Promise<boolean> {
    return this.openOnExternalPage(url);
  }

  redirectToPage(url: string): Promise<any> {
    return this.openOnSamePage(url);
  }

  redirectToCustomDeeplinkWithReplace(link: string): void {
    this.replaceToLink(link);
  }

  replaceToLink(link: string): void {
    this.window.location.replace(link);
  }

  redirectToCustomDeeplink(link: string): void {
    this.openOnSamePage(link);
  }

  redirectToFilmOrPlaylist(): Promise<any> {
    const filmID = this.storageService.get(OUTLAW_COOKIES.return_film_id);
    const postId = this.storageService.get(OUTLAW_COOKIES.return_post_id);

    const playlistID = this.storageService.get(OUTLAW_COOKIES.return_playlist_id);
    const returnUrl = this.storageService.useOnce(OUTLAW_COOKIES.return_url);
    const subscribePagePostId = this.storageService.get(OUTLAW_COOKIES.return_subscribe_page);

    if (subscribePagePostId) {
      return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.subscribe)], {
        queryParams: { postId: subscribePagePostId, from_auth: true },
      });
    }

    if (playlistID && filmID) {
      return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.playlists)], {
        queryParams: { playlistID, filmID },
      });
    } else if (playlistID) {
      return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.playlists)], {
        queryParams: { playlistID },
      });
    } else if (postId) {
      return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.post)], {
        queryParams: { postId },
      });
    } else if (filmID) {
      return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.films)], {
        queryParams: { filmID },
      });
    } else if (returnUrl) {
      return this.openOnSamePage(returnUrl);
    }
    return this.router.navigate(['/']);
  }

  redirectToSavedOrHome(): void {
    const path = OUTLAW_COOKIES.path_before_registration;
    const rootUrl = '/';
    const savedPath = this.storageService.get(path) || this.storageService.useOnce(OUTLAW_COOKIES.return_url);
    if (savedPath) {
      this.storageService.remove(path);
      const parsedSavedPath = this.router.parseUrl(savedPath);
      const savedUrl = parsedSavedPath.root.children['primary']?.segments.map(segment => segment.path).join('/');
      const queryParams = parsedSavedPath.queryParams;
      const redirectUrl = savedUrl ? rootUrl + savedUrl : rootUrl;
      this.router.navigate([redirectUrl], { queryParams });
    } else {
      this.redirectToFilmOrPlaylist();
    }
  }

  goToFeed(): void {
    this.navigateTo(['/']);
  }

  goToFeedOrSaved(): void {
    this.redirectToSavedOrHome();
  }

  replaceToFeed(): void {
    this.replaceToLink('/');
  }

  replaceToFilmmaker(id: number): void {
    if (id) {
      const route = this.router.createUrlTree([getRoutePathFor(OUTLAW_ROUTES.filmmaker)], {
        queryParams: { creatorID: id },
      });
      this.replaceToLink(route.toString());
    }
  }

  goToNotFound(): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.notFound)]);
  }

  goToInterests(): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.chooseInterests)]);
  }

  goToAddNickName(): Promise<void> {
    return this.navigateTo([
      getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.registration, OUTLAW_ROUTES.auth.addNickname),
    ]);
  }

  goToFollow(): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.follow)]);
  }

  goToAddPhone(): Promise<any> {
    return this.navigateTo([
      getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.registration, OUTLAW_ROUTES.auth.addPhone),
    ]);
  }

  goToForgotPassword(): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.forgotPassword)]);
  }

  goToPhoneVerify(queryParams: any = {}): Promise<void> {
    return this.navigateTo(
      [getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.registration, OUTLAW_ROUTES.auth.phoneVerify)],
      { queryParams }
    );
  }

  goToLoginByEmail(): Promise<void> {
    return this.navigateTo([
      getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.login, OUTLAW_ROUTES.auth.email),
    ]);
  }

  goToRegistrationByEmail(): Promise<void> {
    return this.navigateTo([
      getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.registration, OUTLAW_ROUTES.auth.email),
    ]);
  }

  goToChannel(id: number): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.channel)], {
      queryParams: { creatorID: id },
    });
  }

  goToFilmmaker(id: number): void {
    this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.filmmaker)], {
      queryParams: { creatorID: id },
    });
  }

  navigateToFilm(id: number): void {
    this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.films)], {
      queryParams: { filmID: id },
    });
  }

  goToRegistration(extra = {}): Promise<any> {
    return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.auth.base, OUTLAW_ROUTES.auth.registration)], {
      queryParams: extra,
    });
  }

  goToSubscriptionPage(extra = {}): Promise<any> {
    return this.router.navigate([getRoutePathFor(OUTLAW_ROUTES.subscribe)], {
      queryParams: extra,
    });
  }

  goToConnectDevice(): Promise<void> {
    return this.navigateTo([getRoutePathFor(OUTLAW_ROUTES.settings, OUTLAW_ROUTES.devices)]);
  }

  navigateTo(route: string[], params: NavigationExtras = null): Promise<any> {
    if (params) {
      return this.router.navigate(route, params);
    }
    return this.router.navigate(route);
  }

  async openSocialLink(provider: string, uuid: string, platform: string = ''): Promise<void> {
    let url = `${this.socialAuthUrl}/${provider}/${uuid}/`;
    if (platform) {
      url += `?client_platform=${platform}`;
    }
    await this.openOnSamePage(url);
  }

  redirectToCommunityAdminPanel(creatorUrl: string): void {
    this.openOnExternalPage(creatorUrl);
  }
}
